import { useToast, useDisclosure, Checkbox } from "@chakra-ui/react";
import { Box, Center, Flex, HStack, Stack, Text, VStack } from "@chakra-ui/layout";
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";

import { BCAPDFSignatureBoxType, BCASignaturePosition } from "bca-client-signature/lib/BCAPDFSignatureZones";
import { BCADocumentFile, BCAMemoryDocumentFile } from "bca-client-signature/lib/BCADocuments";
import { createQSCDSigner, saveFileToDevice, setBCALicence, setIdopteChallengeURL, setIdopteWebappCert } from "bca-client-signature/lib/BCAQSCD";
import { BCAQSCDBackend, BCAQSCDSession } from "bca-client-signature/lib/BCAQSCDBackend";
import { BCACertificateStatus, BCAQSCDCertificateInfo, BCAReturnCode, BCASignatureOptions, BCASigningLevel, BCAVisibleSignatureOptions } from "bca-client-signature/lib/BCAQSCDTypes";
import { $map } from "foundation-ts/array";
import { $count, $defined, $isfunction, $isnumber, $isuuid, $length, $ok, $string, $url, $value } from "foundation-ts/commons";
import { $uuid } from "foundation-ts/crypto";
import { $decodeBase64, $uint8ArrayFromDataLike } from "foundation-ts/data";
import { TSColor } from "foundation-ts/tscolor";
import { TSDate } from "foundation-ts/tsdate";
import { TSDateForm } from "foundation-ts/tsdatecomp";
import { $bool } from "foundation-ts/tsparser";
import { Nullable, TSDataLike, TSDictionary, uint, UUID } from "foundation-ts/types";
import { $inspect } from "foundation-ts/utils";
import { ApprovalDto, DocumentDto, SignatureDto, UploadFileBody, UserDto } from "doxecure-commons/doxecureClientTypes";
import { ApprovalStatus, SessionStatus, SignaturePhase, SignatureStatus, SignatureType } from "doxecure-commons/doxecureModelTypes";
import { DocumentSignaturePosition, SessionVersion, SignatureMode, UploadedDocumentFileStructure } from "doxecure-commons/doxecureTypes";

import { ColumnTitle } from "@components/ColumnTitle/ColumnTitle";
import { G1Button } from "@components/G1Button";
import { G1DateInput } from "@components/G1DateInput";
import { AsyncConfirmModal, AsyncConfirmModalState, DefaultAsyncConfirmModalState } from "@components/Modals/AsyncConfirmModal";
import { AsyncRefuseModal } from "@components/Modals/AsyncRefuseModal";
import { AsyncSelectModal } from "@components/Modals/AsyncSelectModal";
import { SignatureModal } from "@components/Modals/SignatureModal";
import { WaitingModal } from "@components/Modals/WaitingModal";
import { SeaShellBox } from "@components/SeaShellBox";
import { Viewer } from "@components/Viewer/Viewer";
import { config } from "@/config/config";
import { MAX_DURING_TIME } from "@containers/signature/SignatureBox";
import { useDevice } from "@hooks/useDevice";
import useSession from "@hooks/useSession";
import { DocumentContentID, SectionsListQueryID } from "@hooks/hooksConstants";
import { approveSession, getDocumentRepresentation, refuseSession, sendOTP, signDocuments } from "@services/session.service";
import apiClient, { g1call } from "@services/apiClient";
import { $g1Color } from "@/theme/G1Style";
import { BCADownloadDocumentFile } from "@utils/BCADownloadDocumentFile";
import { bytesToDownloadURL, canSessionBeSigned, optlog, trimOnWords, trueOrEmptyCard } from "@utils/functions";
import { G1Icon } from "@utils/icons";
import { localformat, localtrans } from "@utils/LocalTranslations";
import { defaultErrorToast, defaultSuccessToast } from "@utils/toast";
import { RemainingSigners } from "@utils/viewPDFTypes";

interface DocumentsViewerProps {
    validityDate: string ;
    senderName: string ;
    immutableDocuments: boolean ;
    acceptsSignature: boolean ;
    sessionId?: UUID ;
    profileId?: Nullable<UUID>;
    token?: Nullable<string> ;
    signatureMode?: SignatureMode ;
    resetAndBack?: () => void ;
    deleteSession?: () => void ;
    editSession?: () => void ;
    extendEndSessionDate?: (newDate: Nullable<TSDate>) => void;
    onExternalSignature?: () => void;
}

export const DocumentsViewer = (props:DocumentsViewerProps) => {
    const { sessionId, profileId, senderName, immutableDocuments, acceptsSignature, token, signatureMode, deleteSession, editSession, extendEndSessionDate, onExternalSignature, resetAndBack } = props ;
    const { data:session, isError, isLoading } = useSession(sessionId, profileId, token) ;
    const [visibleDocuments, setVisibleDocuments] = useState<DocumentDto[]>([]) ;
    const [signatures, setSignatures] = useState<SignatureDto[]>([]) ;
    const [refusedSignatures, setRefusedSignatures] = useState<SignatureDto[]>([]) ;
    const [futureSignatures, setFutureSignatures] = useState<SignatureDto[]>([]) ;
    const [ownerSignature, setOwnerSignature] = useState<SignatureDto|undefined>(undefined) ;
    const [approvals, setApprovals] = useState<ApprovalDto[]>([]);
    const [refusedApproval, setRefusedApproval] = useState<Nullable<ApprovalDto>>(null);
    const [futureApprovals, setFutureApprovals] = useState<ApprovalDto[]>([]);
    const [ownerApproval, setOwnerApproval] = useState<Nullable<ApprovalDto>>(null);
    const [isExpiredSession, setIsExpiredSession] = useState(false);
    const [isCanceledSession, setIsCanceledSession] = useState(false);
    const [signatureLink, setSignatureLink] = useState('') ;
    const [backend, setBackend] = useState<Nullable<BCAQSCDBackend>>(undefined) ;
    const [certificates, setCertificates] = useState<BCAQSCDCertificateInfo[]>([]) ;
    const [isOpenRefuseModal, setIsOpenRefuseModal] = useState(false) ;
    const [isOpenSignatureModal, setIsOpenSignatureModal] = useState(false);
    const [creatorOrExpeditor, setCreatorOrExpeditor] = useState(false) ;
    const [signerPositions, setSignerPositions] = useState<DocumentSignaturePosition[]|undefined>(undefined);
    const [remainingSigners, setRemainingSigners] = useState<RemainingSigners[]|undefined>(undefined);
    const [signingVisibleOptions, setSigningVisibleOptions] = useState<Nullable<BCAVisibleSignatureOptions>>(null);
    const [editEndSessionDate, setEditEndSessionDate] = useState(false);
    const [newEndSessionDate, setNewEndSessionDate] = useState<Nullable<TSDate>>(null);
    const [confirmReadDoc, setConfirmReadDoc] = useState<boolean>(false);
    const [waitMessage, setWaitMessage] = useState('') ;
    const [confirmModal, setConfirmModal] = useState<AsyncConfirmModalState>(DefaultAsyncConfirmModalState);
    const viewerRef = useRef<HTMLIFrameElement>(null);
    const toast = useToast();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { isOpen, onOpen, onClose } = useDisclosure() ;
    const { isMOBILE } = useDevice() ;
    const now = TSDate.zulu() ;
    const topAnchor = "DocumentsViewerTopAnchor" ;

    if ($ok(config.licence.cardbridge)) { setBCALicence(config.licence.cardbridge.appId, config.licence.cardbridge.token); }
    if ($ok(config.licence.scws)) { 
        setIdopteWebappCert(config.licence.scws.webappcert) ;
        if ($length(config.licence.scws.challengeUrl) !== 0) { setIdopteChallengeURL(config.licence.scws.challengeUrl) ; }
    } 

    const handleSignature = async (b:BCAQSCDBackend, certificate:BCAQSCDCertificateInfo) => {
        setConfirmModal({
            title:"Signature de documents",
            message: [
                "En cliquant sur signer vous allez procéder à la signature de l'ensemble des documents",
                `avec le certificat de signature ${certificate.commonName} délivré par ${certificate.issuerName}.`,
                "En effectuant cette signature vous actez le fait d'avoir pris connaissance de ces documents."
            ].join(' '),
            cancel:localtrans('cancel'),
            confirm:localtrans('Sign'),
            onClose: async () => {
                setConfirmModal(DefaultAsyncConfirmModalState) ;
                _cancelSignatureProcess() ;
            },
            onAccept: async () => {
                setConfirmModal(DefaultAsyncConfirmModalState) ;
                await handleSignatureProcess(b, certificate.identifier) ;
            },
        });
    };
    
    const _fileDownloadURL = (url:string, fileName:string) => {
        optlog('>>> >>> >>> >>> +++') ;
        optlog('want to download file', fileName) ;
        const link = document.createElement('a') ;
        link.href = url;
        link.download = fileName ;
        link.click();        
    }

    const _linkAction = (actionUrl:string, download?:boolean) => {
        if ($length(actionUrl) > 0) {
            const link = document.createElement('a') ;
            optlog('>>> >>> >>> >>> +++') ;
            if (!download) {
                optlog('want to open new link', actionUrl) ;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';    
            }
            else {
                optlog('want to download link', actionUrl) ;
            }
            link.href = actionUrl;
            link.click();        
        }
    }

    const onChangeSessionDate = (newDate: Nullable<TSDate>) => {
        setNewEndSessionDate(newDate);
        return $value(newDate, undefined);
    }

    const onCancelEditSessionDate = () => {
        setNewEndSessionDate(null);
        setEditEndSessionDate(false);
    }

    const handleAction = async (action:Action, info?: Nullable<TSDictionary>): Promise<void> => {
        optlog(`Did make action ${action}`) ;
        switch (action) {
            case Action.modify:
                if ($isfunction(editSession)) { editSession!(); }
                break;

            case Action.delete:
                if ($isfunction(editSession)) { deleteSession!(); }
                break;

            case Action.extendEndSessionDate:
                if ($isfunction(extendEndSessionDate)) { extendEndSessionDate!(newEndSessionDate); }
                break;

            case Action.download:
                let fileToDownload: BCADocumentFile|undefined = undefined ;
                if ($length(info?.filename) !== 0 && $length(info?.urlData) !== 0) {
                    // we have a filename and a data comming from the iframe document viewer
                    const data = $decodeBase64(info!.urlData.split(",")[1]) ;
                    if ($length(data)) {
                        fileToDownload = new BCADownloadDocumentFile(info!.filename, data) ;
                    }
                } else if ($length(info?.urlData) === 0) {
                    // we need to fetch the zip to download from the backend
                    // only if info.urlData is not set, which would mean we have some sort 
                    // of other data to downlaod and we don't know its name
                    const ret = await g1call('download zip of signed documents', c => c.downloadSignedDocuments(session!.apid!)) ;
                    if ($count(ret) && $length(ret![0])) {
                        fileToDownload = new BCADownloadDocumentFile(`${session!.title}.zip`, ret![0]!) ;
                    }
                }
                if (!$ok(fileToDownload)) {
                    toast(defaultErrorToast(localtrans('ImpossibleToDownloadDocuments'))) ;
                    return ;
                }

                const ret = await saveFileToDevice(fileToDownload!) ; // here we try to use our internal save document system
                if (!$defined(ret) || (ret === BCAReturnCode.NotImplemented)) {
                    // if it does not work : back to internal browser download mechanism 
                    const dataLike = await fileToDownload!.toDataLike() ;
                    const url: Nullable<string> = $ok(dataLike) ? bytesToDownloadURL($uint8ArrayFromDataLike(dataLike!)) : undefined ;
                    if ($length(url) === 0) { toast(defaultErrorToast(localtrans('ImpossibleToDownloadDocuments'))) ; }
                    else { _fileDownloadURL(url!, fileToDownload!.fileName) ; }
                }
                break ;

            case Action.sign:
                // TODO - To be removed when no anymore first session version to be signed
                if (session?.version === SessionVersion.FirstVersion) {
                    if (ownerSignature?.phase === SignaturePhase.Server) {
                        _linkAction(signatureLink) ;
                        if ($isfunction(onExternalSignature)) { onExternalSignature!() ;}
                    }
                    else if ($ok(ownerSignature)) {
                        optlog('QSCD >>> >>> >>> >>> +++') ;
                        setWaitMessage(localtrans('WaitingForCertificatesList')) ;
                        onOpen();
                        BCAQSCDBackend.debug = (trace, args) => optlog(trace, args);
                        const manager = await createQSCDSigner({
                            applicationName:config.app.title,
                            timeouts: config.timeouts,
                        }) ;                   
                        setBackend(manager) ;
                        if ($ok(manager)) {
                            optlog('Connected to backend system') ;
                            const certifFilter = $bool(process.env.QSCD_BYPASS_CERTIF) ? async (c: BCAQSCDCertificateInfo) => { return Promise.resolve(c.status <= BCACertificateStatus.mayBeValid) } : null;
                            const managerCertificates = await manager?.getCertificates(certifFilter) ;
                            onClose(); 
                            const n = $count(managerCertificates) ;
                            if (n > 0) {
                                if (n === 1) {
                                    await handleSignature(manager!, managerCertificates!.first()!) ;
                                }
                                else {
                                    // we have to select the certificates inside a modal window
                                    // this will open the modal window
                                    setCertificates(managerCertificates!) ;
                                }
                            } else { 
                                toast(defaultErrorToast(localtrans('CertificatesNotFound'))) ;
                            }
                        } else { 
                            onClose();
                            toast(defaultErrorToast(localtrans('SigningDeviceNotConnected'))) ;
                        }
                    }
                } else if ((info?.userChoice === SignatureType.QSCD) || ownerSignature?.signatureType === SignatureType.QSCD) {
                    optlog('QSCD >>> >>> >>> >>> +++') ;
                    setWaitMessage(localtrans('WaitingForCertificatesList')) ;
                    onOpen();
                    BCAQSCDBackend.debug = (trace, args) => optlog(trace, args);
                    const manager = await createQSCDSigner({
                        applicationName:config.app.title,
                        timeouts: config.timeouts,
                    }) ;                   
                    setBackend(manager) ;
                    if ($ok(manager)) {
                        optlog('Connected to backend system') ;
                        const certifFilter = $bool(process.env.QSCD_BYPASS_CERTIF) ? async (c: BCAQSCDCertificateInfo) => { return Promise.resolve(c.status <= BCACertificateStatus.mayBeValid) } : null;
                        const managerCertificates = await manager?.getCertificates(certifFilter) ;
                        onClose(); 
                        const n = $count(managerCertificates) ;
                        if (n > 0) {
                            if (n === 1) {
                                await handleSignatureProcess(manager!, managerCertificates!.first()!.identifier);
                            } else {
                                // we have to select the certificates inside a modal window
                                // this will open the modal window
                                setCertificates(managerCertificates!) ;
                            }
                        } else { 
                            toast(defaultErrorToast(localtrans('CertificatesNotFound'))) ;
                        }
                    } else { 
                        onClose();
                        toast(defaultErrorToast(localtrans('SigningDeviceNotConnected'))) ;
                    }
                } else {
                    setIsOpenSignatureModal(true);
                }
                break ;

            case Action.refuse:
                setIsOpenRefuseModal(true) ;
                break ;

            case Action.cancel:
                setConfirmModal({
                    title:"Annulation de la session",
                    message:"Êtes-vous bien sur de vouloir annuler cette session ?",
                    confirm:"Oui, j'annule",
                    onClose: async () => { setConfirmModal(DefaultAsyncConfirmModalState) ; },
                    onAccept: async () => {
                        handleCancelSession() ;
                        setConfirmModal(DefaultAsyncConfirmModalState) ;
                    }
                }) ;
                break ;

            case Action.createTemplate:
                navigate(`/app/newTemplate/${session?.apid}`) ;
                break ;

            case Action.approve:
                setConfirmModal({
                    title: localtrans("DocumentsViewer.ApprovalConfirmationModalTitle"),
                    message: localtrans("DocumentsViewer.ApprovalConfirmationModalDesc"),
                    cancel: localtrans("cancel"),
                    confirm: localtrans("approve"),
                    onClose: async () => {
                        setConfirmModal(DefaultAsyncConfirmModalState) ;
                    },
                    onAccept: async () => {
                        setConfirmModal(DefaultAsyncConfirmModalState) ;
                        setWaitMessage(localtrans('DocumentsViewer.WaitingApproval'));
                        onOpen();
                        const success = await approveSession(sessionId);
                        onClose();
                        if (success) {
                            const isLastApprover = ownerApproval?.rank === ($count(session?.approvals)-1);
                            const nextAction = localtrans(isLastApprover ? "DocumentsViewer.ProcessSignatureAction" : "DocumentsViewer.NextApprovalAction");
                            toast(defaultSuccessToast(localformat("DocumentsViewer.SucceededApproveSession", { nextAction })));
                            navigate(`/app/${isLastApprover ? 'tobeSignedDocuments' : 'tobeApprovedDocuments'}`);
                        } else {
                            toast(defaultErrorToast(localtrans("DocumentsViewer.FailedApproveSession")));
                        }
                    },
                });
                break;

            default:
                optlog("Unknown action") ;
                break ;
        }
    } ;

    const _cancelSignatureProcess = () => {
        setCertificates([]) ;
        setBackend(undefined) ;
        toast(defaultErrorToast(localtrans('LocalSignatureWasCancelled'))) ; 
    } ;

    const handleRefuse = async(reason: string) => {
        setWaitMessage(localtrans('WaitingForSignatureRefused')) ;
        onOpen() ;
        const refusalDate = TSDate.zulu() ;
        
        optlog('############################################') ; 
        optlog('sessionID', session?.apid) ;
        optlog('signatureID', ownerSignature?.apid) ;
        optlog('signerID', ownerSignature?.signer?.apid) ;
        optlog('profileID', profileId) ;
        optlog('ownerSignature', ownerSignature) ;
        optlog('refusalDate', refusalDate) ;
        optlog('refusalReason', reason) ;
        optlog('############################################') ; 

        const done = session?.status === SessionStatus.ToBeApproved ? await refuseSession(session!.apid!, reason) : await g1call('refuse signature', (c) => c.refuseSignature(session!.apid!, ownerSignature!.apid!, ownerSignature!.signer!.apid!, refusalDate, reason, token)) ;
        onClose() ;
        if (!!done) {
            queryClient.invalidateQueries([DocumentContentID, SectionsListQueryID]) ;
            toast(defaultSuccessToast(localtrans(`DocumentsViewer.${session?.status === SessionStatus.ToBeApproved ? 'ApprovalWasRefused' : 'SignatureWasRefused'}`))) ;
            if ($ok(token)) {
                await g1call('expire token access', (c) => c.deleteSession(session!.apid!, token)) ;
                navigate('/result-external-sign?ret=0') ;
            } else {
                navigate(`/app/refusedDocuments`, { replace: true });
            }
        }
        else {
            toast(defaultErrorToast(localtrans(`DocumentsViewer.${session?.status === SessionStatus.ToBeApproved ? 'ApprovalCouldNotBeRefused' : 'SignatureCouldNotBeRefused'}`))) ; 
        }
    } ;

    const handleCancelSession = async() => {
        setWaitMessage(localtrans('CancellingCurrentSession')) ;
        onOpen() ;
        const cancelDate = TSDate.zulu() ;

        optlog('############################################') ; 
        optlog('sessionID', session?.apid) ;
        optlog('profileID', profileId) ;
        optlog('refusalDate', cancelDate) ;
        optlog('############################################') ; 
        
        const done = await g1call('cancel signature', (c) => c.abortSession(session!.apid!)) ;
        onClose() ;
        if (!!done) {
            queryClient.invalidateQueries([DocumentContentID, SectionsListQueryID]) ;
            toast(defaultSuccessToast(localtrans('SessionWasCancelled'))) ;
            navigate(`/app/canceledDocuments`, { replace: true });
        } else {
            toast(defaultErrorToast(localtrans('SessionCouldNotBeCancelled'))) ; 
        }
    }

    const handleSendOTP = async (email?: Nullable<string>): Promise<boolean> => {
        if (!$ok(email)) {
            toast(defaultErrorToast("Impossible d'envoyer le code OTP - Aucun email fourni."));
            return false;
        }

        const success = await sendOTP(session?.apid, ownerSignature?.apid, "EMAIL", email!, token);
        if (!success) {
            toast(defaultErrorToast("L'envoi du code OTP a échoué, veuillez réessayer."));
        }
        return success;
    }

    const handleSignWithOTP = async (otp: string): Promise<Nullable<string>> => {
        try {
            if (await signDocuments(session?.apid, ownerSignature?.apid, profileId, otp, token)) {
                toast(defaultSuccessToast(localtrans('LocalSignatureWasDone')));
                if ($ok(token)) {
                    await g1call('expire token access', (c) => c.deleteSession(session!.apid!, token)) ;
                    navigate('/result-external-sign?ret=1') ;
                } else {
                    navigate((futureSignatures.length === 1) ? `/app/signedDocuments` : `/app/tobeSignedDocuments`, { replace: true });
                }
                return null;
            } else {
                return "La signature des documents a échoué";
            }
        } catch (e) {
            return (e as Error).message === "400" ? "Code OTP incorrect" : "Une exception a été levée lors de la signature des documents";
        }
    }

    const handleSignatureProcess = async (b: BCAQSCDBackend, certificateID: string) => {
        setWaitMessage(localtrans('WaitingForLocalSignature')) ;
        onOpen() ;
        const done = await _proceedToSignature(b, certificateID) ;
        onClose() ;
        if (done) {
            queryClient.invalidateQueries([DocumentContentID, SectionsListQueryID]) ;
            toast(defaultSuccessToast(localtrans('LocalSignatureWasDone')))
            setBackend(undefined) ;
            setCertificates([]) ;
            if ($ok(token)) {
                await g1call('expire token access', (c) => c.deleteSession(session!.apid!, token)) ;
                navigate('/result-external-sign?ret=1') ;
            } else {
                navigate((futureSignatures.length === 1) ? `/app/signedDocuments` : `/app/tobeSignedDocuments`, { replace: true });
            }
        }
        else { _cancelSignatureProcess() ; }
    }

    const _proceedToSignature = async (b: BCAQSCDBackend, certificateID: string):Promise<boolean> => {
        const errors:string[] = [] ;
        function error(e:string):boolean { errors?.push(e) ; toast(defaultErrorToast(e)) ; return false ; }

        if (!$ok(b)) { return error("Le système de signature n'est plus accessible.") ; }
        if (!$isuuid(session?.apid)) { return error('Erreur interne: impossible de trouver la session de signature') ; }
        if (!$isuuid(ownerSignature?.apid)) { return error('Erreur interne: impossible de trouver le signataire') ; }

        const isLastSigner = $count(session?.signatures) - $count(session?.signatures?.filter(sign => sign.signatureStatus === SignatureStatus.Signed)) === 1;
        const documentsCount = $count(visibleDocuments) ;
        if (!documentsCount) { return error("Erreur interne: impossible de lister les documents à signer.") ; }
        if (visibleDocuments!.filter(d => !$ok(d.apid))?.length > 0) { return error("Erreur interne: certains documents ne sont pas complets.") ; }

        const signatureOptions = getSignatureOptions(token);
        if (session?.version === SessionVersion.FirstVersion) {
            const serverSignature = session!.signatures!.filter(sign => sign.phase === SignaturePhase.Server).first() ;
            if ($ok(serverSignature) && (session!.signatures!.indexOf(ownerSignature!) < session!.signatures!.indexOf(serverSignature!))) {
                signatureOptions.signingLevel = BCASigningLevel.LT ;
            }
        }

        const localSessionRet = await b.openSignatureSession(certificateID, signatureOptions, errors) ;
        if ($isnumber(localSessionRet)) { 
            return error(_signErrorMessage(localSessionRet as BCAReturnCode)) ;
        }
        const localSession = localSessionRet as BCAQSCDSession;

        let localError = '';
        const signedDocuments: UploadedDocumentFileStructure[] = [] ;
        try {
            // TODO => review this !
            const loginRet = await b.login(config.timeouts.loginTimeout);
            if (loginRet === BCAReturnCode.PinCanceled) {
                return false;
            } if (loginRet !== BCAReturnCode.OK) {
                return error(_signErrorMessage(loginRet));
            }

            for (let i = 0; i < documentsCount && localError.length === 0; i++) {
                const d = visibleDocuments![i] ;
                const representation = await getDocumentRepresentation(session!.apid!, d.apid!, token);
                const bcaDocument = new BCAMemoryDocumentFile(d.fileName, representation.pdfUrlData.replace('data:application/pdf;base64,', '').decodeBase64());

                optlog('------------------------------------------------------') ;
                optlog('ownerSignature', ownerSignature)
                optlog('visible signatures', representation.visibleSignatures) ;
                optlog('------------------------------------------------------') ;
                let signret: BCADocumentFile|BCAReturnCode;
                if (session?.signatures?.first()?.signatureMode === SignatureMode.Visible) {
                    const position = representation.visibleSignatures?.find(r => r.identifier === ownerSignature?.signer?.apid) ;
                    if (!$ok(position)) { 
                        localError = `Erreur interne: impossible de trouver la position de la signature sur un des documents.` ;
                        continue;
                    } else {
                        const signPosition:BCASignaturePosition = { ... position!, page:position!.page + 1 } ;
                        const visibleSignatureOptions: BCAVisibleSignatureOptions = {
                            position: signPosition,
                            namePrefix: "Signé par"
                        };
                        if (session.version !== SessionVersion.FirstVersion) {
                            visibleSignatureOptions.boxStyle = defaultVisibleOptions.boxStyle;
                            visibleSignatureOptions.signaturePolicy = defaultVisibleOptions.signaturePolicy;
                        }
                        signret = await localSession.signPDF(bcaDocument, visibleSignatureOptions, errors) ;
                    }
                } else {
                    signret = await localSession.invisibleSignPDF(bcaDocument, null, errors) ;
                }
                if ($isnumber(signret)) { 
                    localError = _signErrorMessage(signret as BCAReturnCode);
                    continue;
                }
                let signedDocument = signret! as BCADocumentFile;

                // If this is the last signer and signatures are in LT level then we add a last timestamp to have LTA signatures
                if (session?.version !== SessionVersion.FirstVersion && isLastSigner) {
                    signret = await localSession.archivePDF(signedDocument, async (requestBody: TSDataLike, _timeout?: Nullable<uint>): Promise<Nullable<TSDataLike>> => { 
                        const tsBase64Encoded = await apiClient.requestTimestamp(requestBody, true, token) ;
                        return $ok(tsBase64Encoded) ? $decodeBase64(tsBase64Encoded!) : null ;
                    }, errors);
                    if ($isnumber(signret) && signret !== BCAReturnCode.NotSignedWithLTLevel) {
                        localError = _signErrorMessage(signret as BCAReturnCode);
                        continue;
                    }
                    signedDocument = (signret !== BCAReturnCode.NotSignedWithLTLevel) ? signret! as BCADocumentFile : signedDocument;
                }

                // here we have the base64 representation of the PDF
                let signedData = await signedDocument.toBase64();
                if (!$ok(signedData)) {
                    localError = `Impossible de récupérer les données du document signé ${d.fileName}`;
                    continue;
                }

                const body:UploadFileBody = { source: signedData!, fileName:`${$uuid()}.pdf`} ;
                const uploadedFile = await g1call('upload file', (c) => c.uploadFile(body, sessionId, token)) ;                            
                if (!$ok(uploadedFile)) { 
                    localError = "Impossible de transférer le fichier PDF signé." ;
                } else { 
                    signedDocuments.push({...uploadedFile!, apid:d.apid!})
                    optlog('+++++++++++++++++++++++++++++++++++') ;
                    optlog('did upload signed document', uploadedFile)
                    optlog('+++++++++++++++++++++++++++++++++++') ;
                }
            }
        } catch (e) {
            localError = `erreur durant le process de signature (${(e as Error).message})` ;
        }

        localSession.closeSession() ;
        if (localError.length !== 0) { return error(localError) ; }

        optlog('------------------------------------------------------') ;
        optlog('Did properly close QSCD signature session')
        optlog('session.apid', session!.apid!)
        optlog('profileId', profileId!)
        optlog('------------------------------------------------------') ;
        const signatureIsComplete = await g1call('update signed documents', c => c.updateSignedDocuments(session!.apid!, ownerSignature!.apid!, profileId!, TSDate.zulu(), signedDocuments, null, token)) ;
        if (!signatureIsComplete) {
            // FIXME: if !signatureComplete, we need to remove temporary uploadeded documents
            return error("Une erreur est arrivée lors de la sauvegarde de vos documents signés. L'opération est annulée.")
            //const remainingFiles = await g1call('remove uploaded files', c => c.removeUploadedFiles(signedDocuments)) ;
            //if ($count(remainingFiles)>0) { toast(defaultWarningToast("Certains fichiers temporaires créés lors de l'opération n'ont pas été supprimés.")) ; }
        }
        return !!signatureIsComplete ;
    };

    useEffect(() => {
        optlog(`--- DocumentsViewer initialized with sessionID:'`, $inspect(session?.apid));
        const docs = $value(session?.documents, []);
        setVisibleDocuments(docs);

        const ownerToApprove = session?.approvals?.find(a => a.status === ApprovalStatus.ToApprove && a.approver.apid === profileId);
        const approvalStatusToSearch: ApprovalStatus[] = [ApprovalStatus.None];
        if (!$ok(ownerToApprove)) { approvalStatusToSearch.push(ApprovalStatus.ToApprove); }
        setOwnerApproval(ownerToApprove);
        setApprovals($map(session?.approvals, a => a.status === ApprovalStatus.Approved ? a : undefined));
        setRefusedApproval(session?.approvals?.find(a => a.status === ApprovalStatus.Refused));
        setFutureApprovals($map(session?.approvals, a => approvalStatusToSearch.includes(a.status!) ? a : undefined));
        const alreadySignedSignatures = $map(session?.signatures, s => $ok(s.signatureDate) && s.signatureStatus === SignatureStatus.Signed ? s : undefined);
        setSignatures(alreadySignedSignatures);
        setFutureSignatures($map(session?.signatures, s => !$ok(s.signatureDate) || (s.signatureStatus === SignatureStatus.Canceled) ? s : undefined)) ;
        setRefusedSignatures($map(session?.signatures, s => (s.signatureStatus === SignatureStatus.Refused) ? s : undefined)) ;
        const owner = $ok(session) && !!canSessionBeSigned(session!, profileId) && acceptsSignature ? session!.signatures?.find(s => s.signer.apid === profileId) : undefined ;
        setOwnerSignature(owner) ;
        const link = $value($string($url(owner?.link)), '') ;
        setSignatureLink(link) ;

        const positions: DocumentSignaturePosition[] = [];
        for (const doc of docs) {
            const position = doc.visibleSignatures?.find(sign => sign.identifier === profileId);
            if ($ok(position)) { positions.push(position!); }
        }
        if ($count(positions) > 0) { setSignerPositions(positions); }

        if ($ok(session?.version) && session?.version !== SessionVersion.FirstVersion) {
            const remainingSigners = $map(session?.signatures?.filter(sign => !alreadySignedSignatures.some(s => s.apid === sign.apid)).filter(s => s.apid !== owner?.apid), sign => sign.signer);
            const remainingUsers: RemainingSigners[]|undefined = $count(remainingSigners) > 0 ? [] : undefined;
            for (const document of docs) {
                remainingUsers?.push({
                    docId: document.apid!,
                    signers: $map(remainingSigners.filter(u => document.visibleSignatures?.some(pos => pos.identifier === u.apid)), u => {
                        const infos = { name: `${u.firstName} ${u.lastName}`, id: u.apid!, position: document.visibleSignatures!.find(pos => pos.identifier === u.apid)! }
                        return infos;
                    })
                });
            }
            setRemainingSigners(remainingUsers);
            setSigningVisibleOptions(defaultVisibleOptions);
        }

        setCreatorOrExpeditor(session?.expeditor === profileId || session?.creator === profileId);
        setIsExpiredSession(session?.validity.end.compare(TSDate.zulu()) === -1);
        setIsCanceledSession(session?.status === SessionStatus.Canceled);
        setEditEndSessionDate(false);
        setNewEndSessionDate(null);
    }, [session]);

    return trueOrEmptyCard(
        $ok(profileId) && $ok(sessionId) && $ok(session),
        !visibleDocuments.length ? (
            <VStack h="full">
                <SeaShellBox component="form" height="auto">
                    <VStack align="stretch" width="100%">
                        {($count(futureApprovals) !== 0) && renderFutureUsers(futureApprovals.map(a => a.approver), "Doit être approuvé par :")}
                        {($count(futureSignatures) !== 0) && renderFutureUsers(futureSignatures.map(s => s.signer), "Doit être signé par :")}
                    </VStack>
                    <HStack mt="unset" height="auto" display="flex" justifyContent="center" flexDirection={{ base: "column", sm: "row" }}>
                        { renderButtons(session?.status, creatorOrExpeditor, $ok(ownerApproval), false, $bool(session?.validity.containsDate(now)), $ok(session?.templateId), false, handleAction) }
                    </HStack>
                </SeaShellBox>
                <Text display="flex" flexDirection="column" justifyContent="center" height="100%" textAlign="center" color="lightgrey">
                    Pas de documents dans cette session de signature
                </Text>
            </VStack>
        ) : (
            <>
                {isMOBILE && $isfunction(resetAndBack) &&
                    <ColumnTitle color={$g1Color('global.write')}>
                        <Flex alignItems="center" as="button" onClick={() => resetAndBack!()}>
                            <Center color={$g1Color('global.icons.navigation.previous.write')}><G1Icon.Back /></Center>
                            <Text>Retour</Text>
                        </Flex>
                    </ColumnTitle>
                }

                {/* encart supérieur seulement pour les abonnés */}
                {$ok(token) ?
                    <div id={topAnchor} />
                    : <><SeaShellBox component="form" id={topAnchor} height="auto" mt={{ base: "none", sm: 10 }} mb="5">
                        <HStack mt="unset" height="auto" display="flex" flexDirection={{ base: "column", sm: "row" }}>
                            <VStack align="stretch" width="100%">
                                <Stack align="stretch">
                                    <Text decoration="underline">{(session?.status === SessionStatus.Signed) ? 'Liste des documents signés' : 'Liste des documents à signer'}</Text>
                                    <Text>{`Mis à la signature par ${senderName} ${signatureMode === SignatureMode.Invisible ? "(Signatures non visibles dans le document)" : ""}`}</Text>
                                    {!$ok(ownerSignature) && creatorOrExpeditor && (session?.status === SessionStatus.ToBeApproved || session?.status === SessionStatus.ToBeSigned) && (
                                        <HStack>
                                            <Text>{localformat(editEndSessionDate ? 'DocumentsViewer.EndSessionDateLabel' : 'DocumentsViewer.EndSessionDateBeforeLabel', { end: editEndSessionDate ? "" : session?.validity.end!.toLocaleString("%d/%m/%Y") })}</Text>
                                            {!editEndSessionDate && <G1Icon.Edit onClick={() => setEditEndSessionDate(true)} cursor="pointer" aria-label={localtrans('DocumentsViewer.EditEndSessionButtonTitle')} />}
                                            {editEndSessionDate && (
                                                <>
                                                    <G1DateInput component="form" name="expiredAt" width="150px" mindate={session?.validity.end.dateByAddingDays(1)} maxdate={session?.validity.end.dateByAddingDays(MAX_DURING_TIME)} onDateChange={onChangeSessionDate} />
                                                    <G1Icon.Check aria-label={localtrans("DocumentsViewer.ValidateChangedEndSessionDateTitle")} color="green" size="20px" cursor="pointer" onClick={() => handleAction(Action.extendEndSessionDate)} />
                                                    <G1Icon.Cancel aria-label={localtrans("DocumentsViewer.CancelChangedEndSessionDateButtonTitle")} color="red" size="22px" cursor="pointer" onClick={onCancelEditSessionDate} />
                                                </>
                                            )}
                                        </HStack>
                                    )}
                                    {!$ok(ownerSignature) && (session?.status !== SessionStatus.Signed) && ($count(approvals) !== 0) && renderApprovers(approvals, "Approuvé par :")}
                                    {!$ok(ownerSignature) && (session?.status !== SessionStatus.Signed) && $ok(refusedApproval) && renderApprovers([refusedApproval!], "Refusé par :")}
                                    {!$ok(ownerSignature) && (session?.status !== SessionStatus.Signed) && ($count(futureApprovals) !== 0) && renderFutureUsers(futureApprovals.map(a => a.approver), isExpiredSession || isCanceledSession ? "Approbateurs n'ayant pas approuvé la session :" : "Doit être approuvé par :")}
                                    {!$ok(ownerSignature) && (session?.status !== SessionStatus.Signed) && ($count(signatures) !== 0) && renderSigners(signatures, "Signé par :")}
                                    {!$ok(ownerSignature) && (session?.status !== SessionStatus.Signed) && ($count(refusedSignatures) !== 0) && renderSigners(refusedSignatures, "Refusé par :")}
                                    {!$ok(ownerSignature) && (session?.status !== SessionStatus.Signed) && ($count(futureSignatures) !== 0) && renderFutureUsers(futureSignatures.map(s => s.signer), (refusedSignatures.length !== 0) || isExpiredSession || isCanceledSession ? "Non signataires :" : "Doit être signé par :")}
                                    {(session?.status === SessionStatus.Signed) && !isExpiredSession && <Text fontSize="medium">Vous pouvez récupérer les documents signés jusqu'au {session?.validity.end.toString('%d/%m/%Y')}</Text> }
                                    {$ok(ownerSignature) && <Text>Vous devez consulter l'ensemble des documents présentés ci-dessous pour pouvoir les signer. Si vous éprouvez des difficultés à les consulter entièrement, nous vous conseillons de les visionner en appuyant sur le bouton "Télécharger" de chaque document.</Text>}
                                </Stack>
                            </VStack>

                            <Stack margin="5%" display="flex" justifyContent="center" width={{ base: "100%", sm: "unset" }} height="100%" flexDirection={{ base: "row", sm: "column" }} spacing={{ base: "unset", sm: "2" }} alignItems="center" paddingTop={{ base: "2%", sm: "unset" }}>
                                { renderButtons(session?.status, creatorOrExpeditor, $ok(ownerApproval), ownerSignature?.signer.apid === profileId, $bool(session?.validity.containsDate(now)), $ok(session?.templateId), !confirmReadDoc, handleAction) }
                            </Stack>
                        </HStack>
                    </SeaShellBox>
                    { $ok(ownerApproval) && 
                        <Box height="auto" mt={{ base: "none", sm: 5 }} mb="5" p="2" backgroundColor="carrotOrange.500">
                            <Checkbox isChecked={confirmReadDoc} onChange={(e) => setConfirmReadDoc(e.target.checked)} sx={{
                                '& .chakra-checkbox__control': {
                                    borderWidth: '2px',
                                    borderColor: 'carrotOrange.500',
                                    bg: 'gray.50',
                                    _checked: {
                                        bg: 'blue.500',
                                        borderColor: 'blue.500',
                                    },
                                },
                            }}>
                                {localtrans('DocumentsViewer.ConfirmReadDocCheckbox')}
                            </Checkbox>
                        </Box>
                    }
                    </>
                }

                {$count(visibleDocuments) === 0 ? "" :
                    <Viewer key={session?.apid} viewerRef={viewerRef} documents={visibleDocuments} lastPageSignRows={0 as uint} immutable={immutableDocuments} token={token} signingVisibleOptions={signingVisibleOptions}
                            signerPositions={signerPositions} remainingSigners={remainingSigners}
                            signingView={session?.status === SessionStatus.ToBeSigned && $bool(session?.validity.containsDate(now)) && $ok(ownerSignature)}
                            showRefuseButton={session?.version === SessionVersion.SecondVersion || ((ownerSignature?.phase !== SignaturePhase.Server) && config.app.qscd)}
                            onSignDocuments={() => handleAction(Action.sign)}
                            onRefuseDocuments={() => handleAction(Action.refuse)}
                            onDownloadDocument={(filename, urlData) => handleAction(Action.download, { filename, urlData })} />
                }
                <WaitingModal isOpen={isOpen} onClose={onClose} message={waitMessage}/>
                <AsyncSelectModal 
                    title="Certificats de signature" 
                    message="Sélectionnez votre certificat de signature"
                    items={$map(certificates, c => { 
                        return {
                            label: trimOnWords(c.commonName, 25).capitalize(),
                            labelDesc: `${trimOnWords(c.organization, 20).toUpperCase()} - ${trimOnWords(c.email, 30).toLowerCase()}`,
                            complement1: `Expire le ${c.notAfter.toString(TSDateForm.Standard)}`,
                            complement2: `Autorité: ${trimOnWords(c.issuerName,30)}`,
                            bg: $g1Color('list.certificates.bg'),
                            color: $g1Color('list.certificates.write'),
                            hover: { bg: $g1Color('list.certificates.hover.bg'), color: $g1Color('list.certificates.hover.write') },
                            selected: { bg: $g1Color('list.certificates.select.bg'), color: $g1Color('list.certificates.select.write') }
                        } ;
                    })} 
                    onClose={async() => _cancelSignatureProcess() }
                    onSelect={async(index:number) => {
                        setCertificates([]) ;
                        if (session?.version === SessionVersion.FirstVersion) {
                            await handleSignature(backend!, certificates[index]) ;
                        } else {
                            await handleSignatureProcess(backend!, certificates[index].identifier);
                        }
                    }}
                />
                <AsyncRefuseModal title={localtrans(`DocumentsViewer.${session?.status === SessionStatus.ToBeApproved ? 'RefusalApprovalModalTitle' : 'RefusalSigningModalTitle'}`)} isOpen={isOpenRefuseModal}
                                  onClose={async () => { setIsOpenRefuseModal(false) ; }} onAccept={(reason: string) => {
                    setIsOpenRefuseModal(false) ;
                    setConfirmModal({
                        title: localformat("DocumentsViewer.RefusalConfirmationTitle", { type: localtrans(`DocumentsViewer.${session?.status === SessionStatus.ToBeApproved ? 'RefusalSigningType' : 'RefusalApprovalType'}`) }),
                        message: localtrans(`DocumentsViewer.${session?.status === SessionStatus.ToBeApproved ? 'RefusalApprovalDescription' : 'RefusalSigningDescription'}`),
                        confirm:"Oui, je refuse",
                        onClose: async () => { setConfirmModal(DefaultAsyncConfirmModalState) ; },
                        onAccept: async () => {
                            handleRefuse(reason) ;
                            setConfirmModal(DefaultAsyncConfirmModalState) ;
                        }
                    }) ;
                }} />
                <SignatureModal isOpen={isOpenSignatureModal} emailToConfirm={ownerSignature?.signer.email} onClose={async () => setIsOpenSignatureModal(false)} 
                                onQualifiedSigning={() => handleAction(Action.sign, { userChoice: SignatureType.QSCD })}
                                onSendOTP={handleSendOTP}
                                onSignWithOTP={handleSignWithOTP} />
                <AsyncConfirmModal title={confirmModal.title} message={confirmModal.message} cancel={confirmModal.cancel} confirm={confirmModal.confirm} onClose={confirmModal.onClose} onAccept={confirmModal.onAccept} />
            </>
        ),
        isLoading,
        isError || !$ok(profileId) || !$ok(sessionId) || !$ok(session),
        "Impossible de récupérer les documents de cette session de signature",
        "Connexion invalide",
        "Chargement des documents de la session"
    ) ;
}

function _signErrorMessage(code: BCAReturnCode): string {
    switch(code) {
        case BCAReturnCode.PinTimeout: return "Le code PIN n'a pas été saisi" ;
        case BCAReturnCode.PinCanceled: return "La saisie du code PIN a été annulée" ;
        case BCAReturnCode.PinExpired: return "Code PIN expiré" ;
        case BCAReturnCode.PinLocked: return "Carte bloquée" ;
        case BCAReturnCode.PinFailed: return "Erreur inerne : saisie du code PIN impossible" ;
        case BCAReturnCode.InvalidSession: return "La session de signature a expiré" ;
        case BCAReturnCode.BadCertificateId: return "Erreur interne : format d'identifiant du certificat incorrecte" ;
        case BCAReturnCode.NoTokenFound: return "Erreur interne : impossible d'accéder au token" ;
        case BCAReturnCode.NoSigningCertificateFound: return "Erreur interne : impossible de trouver le certificat de signature" ;
        case BCAReturnCode.NoPrivateKeyFound : return "Erreur interne : impossible d'accéder à la clé privée du certificat" ;
        case BCAReturnCode.SignFailed : return "Erreur interne: levée d'exception durant la signature" ;
        case BCAReturnCode.DisconnectFailed: return "Erreur interne: impossible de se déconnecter du dispositif de signature" ;
        default: return "Erreur interne inconnue" ;
    }
}
enum Action {
    modify,
    extendEndSessionDate,
    delete,
    download,
    sign,
    refuse,
    cancel,
    createTemplate,
    approve,
    sendOTP
} ;

function renderButtons(status:Nullable<SessionStatus>, creatorOrExpeditor: boolean, approver: boolean, signer: boolean, validSession: boolean, fromTemplateId: boolean, disabledApprovalButton: boolean, callback:(action:Action, info?:Nullable<TSDictionary>)=>Promise<void>) {
    const buttonWidth = config.app.templateManagement && creatorOrExpeditor && !fromTemplateId ? "150px" : "100px" ;
    const createTemplateButton = config.app.templateManagement && creatorOrExpeditor && !fromTemplateId ? <G1Button component="form" variant="alternate" title="Créer un modèle" width={buttonWidth} fontSize="md" onClick={async () => callback(Action.createTemplate)} /> : null ;
    if (validSession) {
        // const qscdPhase = signature?.phase !== SignaturePhase.Server;
        // const canSign = $ok(signature) && (!qscdPhase || config.app.qscd);
    
        switch (status) {
            case SessionStatus.Draft:
                return (
                    <>
                        <G1Button component="form" variant="normal" title="Modifier" width={buttonWidth} fontSize="md" onClick={async () => callback(Action.modify)} />
                        <G1Button component="form" variant="delete" title={localtrans('delete')} width={buttonWidth} fontSize="md" onClick={async () => callback(Action.delete)} />
                        { createTemplateButton }
                    </>
                ) ;
            case SessionStatus.Signed:
                return (
                    <>
                        <G1Button component="form" variant="important" title={localtrans('download')} width={buttonWidth} fontSize="md" onClick={() => callback(Action.download)} />
                        { createTemplateButton }
                    </>
                ) ;
            case SessionStatus.ToBeSigned:
                return creatorOrExpeditor && !signer && (
                    <>
                        <G1Button component="form" variant="delete" title={localtrans('cancel')} width={buttonWidth} fontSize="md" onClick={() => callback(Action.cancel)} />
                        { createTemplateButton }
                    </>
                ) ;
            case SessionStatus.ToBeApproved:
                if (creatorOrExpeditor && !approver) {
                    return (
                        <>
                            <G1Button component="form" variant="delete" title={localtrans('cancel')} width={buttonWidth} fontSize="md" onClick={() => callback(Action.cancel)} />
                            { createTemplateButton }
                        </>
                    )
                }

                return approver && (
                    <>
                        <G1Button component="form" variant="normal" title={localtrans('approve')} width={buttonWidth} fontSize="md" disabled={disabledApprovalButton} onClick={() => callback(Action.approve)} />
                        <G1Button component="form" variant="delete" title={localtrans('refuse')} width={buttonWidth} fontSize="md" onClick={() => callback(Action.refuse)} />
                    </>
                );
            case SessionStatus.Refused:
            case SessionStatus.Canceled:
                return (<>{createTemplateButton}</>) ;
            default:
                return null ;
        }
    } else {
        return createTemplateButton ;
    }
}

function renderApprovers(approvals: ApprovalDto[], title: string) {
    return (
        <Box>
            <Text fontSize="medium" decoration="underline">{title}</Text>
            {approvals.map((approval, i) => { 
                const refusalReason =  $ok(approval.refusalReason) ? `pour le motif "${approval.refusalReason}"`: "" ;
                return i > 0 ?
                    (<><br/><Text key={`signer${i}`} fontSize="medium" as="b">{`${approval.approver.firstName} ${approval.approver.lastName} le ${approval.date?.toLocaleString()} ${refusalReason}`}</Text></>)
                    :
                    (<Text key={`signer${i}`}fontSize="medium" as="b">{`${approval.approver.firstName} ${approval.approver.lastName} le ${approval.date?.toLocaleString()} ${refusalReason}`}</Text>)
            })}
        </Box>
    ) ;
}

function renderSigners(signers: SignatureDto[], title: string) {
    return (
        <Box>
            <Text fontSize="medium" decoration="underline">{title}</Text>
            {signers.map((signature, i) => { 
                const date = $ok(signature.refusalDate) ? TSDate.from(signature.refusalDate) : signature.signatureDate ;
                const refusalReason =  $ok(signature.refusalReason) ? `pour le motif "${signature.refusalReason}"`: "" ;
                return i > 0 ?
                    (<><br/><Text key={`signer${i}`} fontSize="medium" as="b">{`${signature.signer.firstName} ${signature.signer.lastName} le ${date?.toLocaleString()} ${refusalReason}`}</Text></>)
                    :
                    (<Text key={`signer${i}`}fontSize="medium" as="b">{`${signature.signer.firstName} ${signature.signer.lastName} le ${date?.toLocaleString()} ${refusalReason}`}</Text>)
            })}
        </Box>
    ) ;
}

function renderFutureUsers(users: UserDto[], title: string) {
    return (
        <Box>
            <Text fontSize="medium" decoration="underline">{title}</Text>
            <Text fontSize="medium" as="b">{users.map((user, i) => `${user.firstName} ${user.lastName}${i < ($count(users)-1) ? ', ' : ''}`)}</Text>
        </Box>
    ) ;
}

const getSignatureOptions = (token: Nullable<string>): BCASignatureOptions => {
    return { 
        signingLevel: BCASigningLevel.B,
        hashMethod: config.backend.hashMethod,
        verifySignatures: config.backend.verifySignatures,
        checkRevocation: config.backend.checkRevocation,
        timestamp: async (requestBody: TSDataLike, _timeout?: Nullable<uint>): Promise<Nullable<TSDataLike>> => { 
            const tsBase64Encoded = await apiClient.requestTimestamp(requestBody, false, token) ;
            return $ok(tsBase64Encoded) ? $decodeBase64(tsBase64Encoded!) : null ;
        },
        downloadCRL: async (url: string, _timeout?: Nullable<uint>): Promise<Nullable<TSDataLike>> => { 
            const crlBase64Encoded = await apiClient.downloadCRL(url, token) ;
            return $ok(crlBase64Encoded) ? $decodeBase64(crlBase64Encoded!) : null ;
        },
        requestOCSP: async (url: string, request: TSDataLike, _timeout?: Nullable<uint>): Promise<Nullable<TSDataLike>> => { 
            const ocspBase64Encoded = await apiClient.requestOCSP(url, request, token) ;
            return $ok(ocspBase64Encoded) ? $decodeBase64(ocspBase64Encoded!) : null ;
        }
    } ;
}

const defaultVisibleOptions: BCAVisibleSignatureOptions = {
    position: {
        identifier: "",
        height: -1,
        width: -1,
        left: -1,
        top: -1,
        page: -1
    },
    boxStyle: {
        type: BCAPDFSignatureBoxType.CeilAndFloor,
        thickness: 2,
        color: TSColor.fromString("#bbb")
    }
};